import { useCallback, useEffect, useState } from 'react'

type ScrollBoundsResult = {
  canScrollLeft: boolean
  canScrollRight: boolean
}

// Tracks horizontal scroll bounds of an element and returns whether the user can scroll left/right
export const useScrollBounds = (scrollRef: React.RefObject<HTMLElement | null>): ScrollBoundsResult => {
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const currentElement = scrollRef.current as HTMLElement

  const updateScrollState = useCallback(() => {
    if (!currentElement) return
    // Get the scroll position, width, and client width of the element
    const { scrollLeft, scrollWidth, clientWidth } = currentElement
    // Check if the element is at the start or end of the scroll
    const isAtStart = scrollLeft <= 0
    const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1
    // Set the state based on the scroll position
    setCanScrollLeft(!isAtStart)
    setCanScrollRight(!isAtEnd)
  }, [currentElement])

  useEffect(() => {
    if (!currentElement) return
    updateScrollState()
    // add the event listener for scroll changes
    currentElement.addEventListener('scroll', updateScrollState)
    // observe the element for resize changes
    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(currentElement)
    return () => {
      // remove the event listener and disconnect the resize observer
      currentElement.removeEventListener('scroll', updateScrollState)
      resizeObserver.disconnect()
    }
  }, [updateScrollState, currentElement])

  return { canScrollLeft, canScrollRight }
}
